import { getTypedValue } from './conflictResolution';
import { VETERAN_INFO_FIELDS, MILITARY_HISTORY_FIELDS } from './fieldMapping';

const FIELD_GROUPS = {
  veteranInfo: VETERAN_INFO_FIELDS,
  militaryHistory: MILITARY_HISTORY_FIELDS,
};

export const resolveFieldGroup = key => {
  const group = FIELD_GROUPS[key];
  if (!group) throw new Error(`Unknown field group: ${key}`);
  return group;
};

/**
 * Determines if a form value is blank/empty (user has not provided input).
 *
 * Contract: A value is considered "blank" if:
 * - It is falsy (null, undefined, 0, false, '', etc.), OR
 * - It is an object where all properties are falsy (e.g., { first: '', middle: '', last: '' })
 *
 * This is used by conflict detection to skip building conflicts for fields the user
 * hasn't filled in. Only values the user has explicitly provided are compared against
 * artifact data to detect conflicts.
 *
 * Note: The normalize() functions in fieldMapping return '' for valid-but-empty user input
 * (e.g., "user cleared the field"), while returning null for unrecognized/invalid artifact data.
 * This function operates on form values BEFORE normalization to identify fields the user
 * hasn't touched, ensuring conflict detection only runs on intentional user input.
 * Note: This function operates on raw form values BEFORE the field's normalize() is
 * applied, to identify fields the user hasn't touched so conflict detection only runs
 * on intentional user input. The null-versus-empty-string contract lives in normalize.js
 * at ingestion, not in the field-level normalize() functions.
 * @param {any} value - The form value to test
 * @returns {boolean} True if the value is blank/unprovided
 */
const isBlankValue = value =>
  !value || (typeof value === 'object' && !Object.values(value).some(Boolean));

const addToGrouped = (
  grouped,
  norm,
  rawValue,
  docTypeLabel,
  fileName,
  trackingKey,
  formatValue,
) => {
  if (!grouped.has(norm)) {
    grouped.set(norm, {
      norm,
      rawValue,
      displayValue: formatValue(rawValue),
      sourceFiles: [{ docTypeLabel, fileName, trackingKey }],
    });
  } else {
    const existing = grouped.get(norm);
    if (!existing.sourceFiles.some(f => f.trackingKey === trackingKey)) {
      existing.sourceFiles.push({ docTypeLabel, fileName, trackingKey });
    }
  }
};

const scanArtifact = (artifact, files, formNorm, fieldDef, grouped) => {
  for (const file of files ?? []) {
    for (const entry of file.idpArtifacts?.[artifact.artifactKey] ?? []) {
      const rawValue = artifact.getArtifactValue(entry);
      if (rawValue) {
        const norm = fieldDef.normalize(rawValue);
        if (norm !== formNorm) {
          addToGrouped(
            grouped,
            norm,
            rawValue,
            artifact.docTypeLabel,
            file.name,
            file.idpTrackingKey,
            fieldDef.formatValue,
          );
        }
      }
    }
  }
};

export const buildConflicts = (formData, files, fieldGroup, groupKey) => {
  const conflicts = [];
  for (const fieldDef of fieldGroup) {
    // Compare against the user's own value: write-through stores a picked
    // scanned value in the field, so derive the user's value from selection
    // state rather than the live field.
    const formValue = getTypedValue(formData, fieldDef, groupKey);
    if (!isBlankValue(formValue)) {
      const formNorm = fieldDef.normalize(formValue);
      const grouped = new Map();

      for (const artifact of fieldDef.artifacts) {
        scanArtifact(artifact, files, formNorm, fieldDef, grouped);
      }

      if (grouped.size > 0) {
        conflicts.push({
          ...fieldDef,
          formValue,
          formDisplayValue: fieldDef.formatValue(formValue),
          artifactOptions: Array.from(grouped.values()),
        });
      }
    }
  }
  return conflicts;
};

export const autoResolveArtifacts = (formData, files, fieldGroup) => {
  if (!files?.length) return files;
  const next = files.map(file => {
    if (!file.idpArtifacts) return file;
    const updatedArtifacts = { ...file.idpArtifacts };
    let changed = false;

    for (const fieldDef of fieldGroup) {
      const formValue = fieldDef.getFormValue(formData);
      if (formValue) {
        for (const artifact of fieldDef.artifacts) {
          const entries = updatedArtifacts[artifact.artifactKey];
          if (entries?.length) {
            const anyValidExists = files.some(f =>
              (f.idpArtifacts?.[artifact.artifactKey] ?? []).some(
                e => !!artifact.getArtifactValue(e),
              ),
            );
            if (!anyValidExists) {
              updatedArtifacts[artifact.artifactKey] = entries.map(entry =>
                artifact.setArtifactValue(entry, formValue),
              );
              changed = true;
            }
          }
        }
      }
    }
    return changed ? { ...file, idpArtifacts: updatedArtifacts } : file;
  });
  // Return same reference if nothing was modified — callers use reference
  // equality to detect whether a Redux dispatch is needed.
  return next.every((f, i) => f === files[i]) ? files : next;
};

// Pure function used by `depends` in form.js.
// Accepts a groupKey string ('veteranInfo' | 'militaryHistory') and resolves
// the field group internally.
export const hasConflicts = (formData, groupKey) =>
  buildConflicts(
    formData,
    formData.files ?? [],
    resolveFieldGroup(groupKey),
    groupKey,
  ).length > 0;

export const hasVeteranInfoConflicts = formData =>
  hasConflicts(formData, 'veteranInfo');

export const hasMilitaryHistoryConflicts = formData =>
  hasConflicts(formData, 'militaryHistory');
